import React, { useState } from "react";
import { supabase } from "../supabase/config";

const formatNotificationTime = (isoString) => {
  if (!isoString) return "";
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

const openEventFromNotification = async (eventId, wasRemoved = false) => {
  const supabaseClient = window.supabase || supabase;
  const { data: eventData } = await supabaseClient
    .from("events")
    .select("*")
    .eq("event_id", eventId)
    .single();
  if (eventData) {
    const formattedEvent = {
      eventId: eventData.event_id,
      title: eventData.title,
      description: eventData.description,
      placeId: eventData.place_id,
      placeName: eventData.place_name,
      placeAddress: eventData.place_address,
      placePhotoUrl: eventData.place_photo_url,
      latitude: eventData.latitude,
      longitude: eventData.longitude,
      startDateTime: eventData.start_date_time,
      endDateTime: eventData.end_date_time,
      peopleLimit: eventData.people_limit,
      creatorId: eventData.creator_id,
      createdBy: eventData.created_by,
      participants: eventData.participants,
      pendingParticipants: eventData.pending_participants,
      customLabel: eventData.custom_label,
      requireApprove: eventData.require_approve,
      pendingRequests: eventData.pending_requests,
      genderPreference: eventData.gender_preference || ['man', 'woman', 'different'],
      category: Array.isArray(eventData.category) ? eventData.category : eventData.category ? [eventData.category] : ['place'],
      ageMin: eventData.age_min || 18,
      ageMax: eventData.age_max || 99,
      timezone: eventData.timezone,
      placeIcon: eventData.place_icon,
    };
    // Only flag as removed if user genuinely still doesn't qualify
    let stillRemoved = false;
    if (wasRemoved) {
      const currentUser = window.currentAppUser || window.currentUser;
      const uid = currentUser?.uid;
      const isNowParticipant = (eventData.participants || []).includes(uid);
      if (!isNowParticipant) {
        // Check if they still fail gender/age
        const userGender = currentUser?.profile?.gender;
        const userDob = currentUser?.profile?.dateOfBirth;
        const newGender = eventData.gender_preference || ['man', 'woman', 'different'];
        const newAgeMin = eventData.age_min || 18;
        const newAgeMax = eventData.age_max || 99;
        let qualifies = true;
        if (userGender) {
          const hasGenderRestriction = !(newGender.includes('man') && newGender.includes('woman') && newGender.includes('different'));
          if (hasGenderRestriction && !newGender.includes(userGender)) qualifies = false;
        }
        if (qualifies && userDob) {
          const age = Math.floor((Date.now() - new Date(userDob)) / (365.25 * 24 * 60 * 60 * 1000));
          if ((newAgeMin !== 18 || newAgeMax !== 99) && (age < newAgeMin || age > newAgeMax)) qualifies = false;
        }
        stillRemoved = !qualifies;
      }
    }
    window.showEventDetailsPanel({ ...formattedEvent, removedFromEvent: stillRemoved }, eventData.place_photo_url);
  } else {
    window.showToast("This event no longer exists", "warning");
  }
};

export default function NotificationsPanel({
  notifications,
  onClose,
  onOpenRequests,
}) {
  const [showDeleteAllConfirm, setShowDeleteAllConfirm] = useState(false);

  const handleNotificationClick = async (notification) => {
    await window.markNotificationAsRead(notification.id);
    onClose();

    if (notification.type === "join_request") {
      onOpenRequests();
    } else if (notification.type === "event_deleted") {
      window.showToast(
        `Event "${notification.event_title}" was cancelled`,
        "warning",
      );
    } else if (notification.event_id) {
      await openEventFromNotification(notification.event_id, notification.type === 'removed_from_event');
    }
  };

React.useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('[data-notifications-panel]') && !e.target.closest('[data-notification-bell]')) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  return (
    <>

{/* Dropdown */}
      <div
        data-notifications-panel
        data-panel="notifications"
        style={{
          position: "fixed",
          top: "79px",
          right: "8px",
          left: "auto",
          width: "360px",
          maxWidth: "calc(100vw - 16px)",
          maxHeight: "500px",
          background: "white",
          borderRadius: "12px",
          boxShadow: "0 8px 32px rgba(0,0,0,0.25), 0 4px 12px rgba(0,0,0,0.15)",
zIndex: 10021,
          animation: "dropdownSlideIn 0.2s ease-out",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "16px 20px",
            borderBottom: "1px solid #e0e0e0",
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <h3
            style={{
              margin: 0,
              fontSize: "16px",
              fontWeight: "600",
              color: "#202124",
            }}
          >
            Notifications
          </h3>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {notifications.some((n) => !n.is_read) && (
              <button
onClick={async (e) => {
                  e.stopPropagation();
                  await window.markAllNotificationsAsRead();
                  onClose();
                }}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: "4px",
                  display: "flex",
                  alignItems: "center",
                  transition: "transform 0.2s",
                }}
                onMouseOver={(e) =>
                  (e.currentTarget.style.transform = "scale(1.1)")
                }
                onMouseOut={(e) =>
                  (e.currentTarget.style.transform = "scale(1)")
                }
                title="Mark all as read"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#4285f4"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                  <circle cx="12" cy="12" r="3"></circle>
                </svg>
              </button>
            )}
            {notifications.length > 0 && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowDeleteAllConfirm(true);
                }}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: "4px",
                  display: "flex",
                  alignItems: "center",
                  transition: "transform 0.2s",
                }}
                onMouseOver={(e) =>
                  (e.currentTarget.style.transform = "scale(1.1)")
                }
                onMouseOut={(e) =>
                  (e.currentTarget.style.transform = "scale(1)")
                }
                title="Delete all notifications"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#ea4335"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="3 6 5 6 21 6"></polyline>
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                  <line x1="10" y1="11" x2="10" y2="17"></line>
                  <line x1="14" y1="11" x2="14" y2="17"></line>
                </svg>
              </button>
            )}
            {/* Close button (always visible — needed on fullscreen native) */}
            <button
              onClick={(e) => { e.stopPropagation(); onClose(); }}
              aria-label="Close notifications"
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: "4px",
                display: "flex",
                alignItems: "center",
              }}
              title="Close"
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#5f6368" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
        </div>

        {/* Delete All Confirmation */}
        {showDeleteAllConfirm && (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(255,255,255,0.95)",
              backdropFilter: "blur(4px)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              padding: "20px",
              zIndex: 10000,
              borderRadius: "12px",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                width: "48px",
                height: "48px",
                background: "#fef7f7",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: "16px",
              }}
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#ea4335"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                <line x1="12" y1="9" x2="12" y2="13"></line>
                <line x1="12" y1="17" x2="12.01" y2="17"></line>
              </svg>
            </div>
            <h4
              style={{
                margin: "0 0 8px 0",
                fontSize: "16px",
                fontWeight: "600",
                color: "#202124",
                textAlign: "center",
              }}
            >
              Delete All Notifications?
            </h4>
            <p
              style={{
                margin: "0 0 24px 0",
                fontSize: "13px",
                color: "#5f6368",
                textAlign: "center",
                lineHeight: "1.5",
              }}
            >
              This will permanently delete all {notifications.length}{" "}
              notification{notifications.length !== 1 ? "s" : ""}. This action
              cannot be undone.
            </p>
            <div
              style={{
                display: "flex",
                gap: "12px",
                width: "100%",
                maxWidth: "280px",
              }}
            >
              <button
                onClick={async (e) => {
                  e.stopPropagation();
                  setShowDeleteAllConfirm(false);
                  await window.deleteAllNotifications();
                }}
                style={{
                  flex: 1,
                  padding: "10px 16px",
                  background: "#ea4335",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor: "pointer",
                }}
                onMouseOver={(e) =>
                  (e.currentTarget.style.background = "#d33828")
                }
                onMouseOut={(e) =>
                  (e.currentTarget.style.background = "#ea4335")
                }
              >
                Delete All
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowDeleteAllConfirm(false);
                }}
                style={{
                  flex: 1,
                  padding: "10px 16px",
                  background: "white",
                  color: "#5f6368",
                  border: "2px solid #dadce0",
                  borderRadius: "8px",
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor: "pointer",
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.background = "#f8f9fa";
                  e.currentTarget.style.borderColor = "#bdc1c6";
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.background = "white";
                  e.currentTarget.style.borderColor = "#dadce0";
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Notifications List */}
        <div data-notif-list style={{ flex: 1, overflowY: "auto", maxHeight: "440px" }}>
          {notifications.length === 0 ? (
            <div
              style={{
                textAlign: "center",
                padding: "32px 20px",
                color: "#5f6368",
              }}
            >
              <svg
                width="48"
                height="48"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#dadce0"
                strokeWidth="1.5"
                style={{ margin: "0 auto 12px" }}
              >
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
                <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
              </svg>
              <p style={{ margin: 0, fontSize: "13px" }}>No notifications</p>
            </div>
          ) : (
            notifications.map((notification) => (
              <div
                key={notification.id}
                onClick={() => handleNotificationClick(notification)}
                style={{
                  padding: "12px 16px",
                  background: notification.is_read ? "white" : "#f8f9fa",
                  borderBottom: "1px solid #f0f0f0",
                  cursor: "pointer",
                  transition: "background 0.2s",
                  display: "flex",
                  gap: "12px",
                  alignItems: "start",
                }}
                onMouseOver={(e) =>
                  (e.currentTarget.style.background = "#f1f3f4")
                }
                onMouseOut={(e) =>
                  (e.currentTarget.style.background = notification.is_read
                    ? "white"
                    : "#f8f9fa")
                }
              >
                {/* Icon */}
                <div
                  style={{
                    width: "32px",
                    height: "32px",
                    borderRadius: "50%",
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background:
                      notification.type === "join_request"
                        ? "#fff3cd"
                        : notification.type === "request_accepted"
                          ? "#e8f5e9"
                          : notification.type === "user_joined"
                            ? "#e3f2fd"
                            : notification.type === "user_left"
                              ? "#fff3e0"
                              : notification.type === "removed_from_event"
                                ? "#fef7f7"
                                : notification.type === "event_deleted"
                                  ? "#fef7f7"
                                  : "#f8f9fa",
                  }}
                >
                  {notification.type === "join_request" ? (
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#856404"
                      strokeWidth="2"
                    >
                      <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                      <circle cx="8.5" cy="7" r="4"></circle>
                      <line x1="20" y1="8" x2="20" y2="14"></line>
                      <line x1="23" y1="11" x2="17" y2="11"></line>
                    </svg>
                  ) : notification.type === "request_accepted" ? (
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#2e7d32"
                      strokeWidth="2"
                    >
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                      <polyline points="22 4 12 14.01 9 11.01"></polyline>
                    </svg>
                  ) : notification.type === "removed_from_event" ? (
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#ea4335"
                      strokeWidth="2"
                    >
                      <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                      <circle cx="8.5" cy="7" r="4"></circle>
                      <line x1="23" y1="11" x2="17" y2="11"></line>
                    </svg>
                  ) : notification.type === "event_deleted" ? (
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#ea4335"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <circle cx="12" cy="12" r="10" strokeWidth="1.5"></circle>
                      <line x1="15" y1="9" x2="9" y2="15"></line>
                      <line x1="9" y1="9" x2="15" y2="15"></line>
                    </svg>
                  ) : notification.type === "user_joined" ? (
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#1976d2"
                      strokeWidth="2"
                    >
                      <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                      <circle cx="8.5" cy="7" r="4"></circle>
                      <polyline points="17 11 19 13 23 9"></polyline>
                    </svg>
                  ) : notification.type === "user_left" ? (
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#ef6c00"
                      strokeWidth="2"
                    >
                      <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                      <circle cx="8.5" cy="7" r="4"></circle>
                      <line x1="23" y1="11" x2="17" y2="11"></line>
                    </svg>
                  ) : null}
                </div>

                {/* Content */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: "13px",
                      fontWeight: "600",
                      color: "#202124",
                      marginBottom: "2px",
                    }}
                  >
                    {notification.title}
                  </div>
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#5f6368",
                      lineHeight: "1.4",
                      marginBottom: "4px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                    }}
                  >
                    {notification.message}
                  </div>
                  <div style={{ fontSize: "11px", color: "#9e9e9e" }}>
                    {formatNotificationTime(notification.created_at)}
                  </div>
                </div>

                {/* Unread dot */}
                {!notification.is_read && (
                  <div
                    style={{
                      width: "8px",
                      height: "8px",
                      borderRadius: "50%",
                      background: "#4285f4",
                      flexShrink: 0,
                      marginTop: "4px",
                    }}
                  ></div>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      <style>{`
        @keyframes dropdownSlideIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </>
  );
}
